import { useEffect, useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, getAuthUser, type BackendPost } from '../lib/api';

type FooterData = {
  col1: { title: string; text: string };
  col2: { title: string; links: { label: string; url: string }[] };
  col3: { title: string; lines: string[] };
};

const defaultData: FooterData = {
  col1: {
    title: "3DSmart",
    text: "Thương hiệu chuyên về giải pháp công nghệ, phụ kiện in 3D và BMCU hỗ trợ kỹ thuật."
  },
  col2: {
    title: "Danh mục",
    links: [
      { label: "BMCU", url: "/bmcu" },
      { label: "Phụ kiện in 3D", url: "/products" },
      { label: "Firmware", url: "/news" },
      { label: "Hỗ trợ", url: "/news" }
    ]
  },
  col3: {
    title: "Liên hệ",
    lines: [
      "support@3dsmart.local",
      "Hotline: 0900 000 000",
      "Địa chỉ: Hà Nội"
    ]
  }
};

export default function EditableFooter() {
  const user = getAuthUser();
  const isAdmin = user?.role === 'ADMIN';
  
  const [post, setPost] = useState<BackendPost | null>(null);
  const [data, setData] = useState<FooterData>(defaultData);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FooterData>(defaultData);
  const [saving, setSaving] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Delay footer display slightly to prevent jumping while page content loads
    const timer = setTimeout(() => setMounted(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const response = await apiRequest<{ posts: BackendPost[] }>('/posts');
        const footerPost = response.posts.find(p => p.slug === 'site-footer');
        if (footerPost) {
          setPost(footerPost);
          const parsed = JSON.parse(footerPost.content);
          setData(parsed);
          setFormData(parsed);
        }
      } catch (error) {
        console.error(error);
      }
    }
    load();
  }, []);

  function handleAddLink() {
    setFormData({
      ...formData,
      col2: { ...formData.col2, links: [...formData.col2.links, { label: '', url: '' }] }
    });
  }

  function handleRemoveLink(idx: number) {
    const newLinks = [...formData.col2.links];
    newLinks.splice(idx, 1);
    setFormData({ ...formData, col2: { ...formData.col2, links: newLinks } });
  }

  function handleAddLine() {
    setFormData({
      ...formData,
      col3: { ...formData.col3, lines: [...formData.col3.lines, ''] }
    });
  }

  function handleRemoveLine(idx: number) {
    const newLines = [...formData.col3.lines];
    newLines.splice(idx, 1);
    setFormData({ ...formData, col3: { ...formData.col3, lines: newLines } });
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: 'Site Footer',
        slug: 'site-footer',
        excerpt: 'Footer config',
        content: JSON.stringify(formData),
        category: 'System',
      };
      let updatedPost: BackendPost;
      if (post) {
        const res = await apiRequest<{ post: BackendPost }>(`/posts/${post.id}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        updatedPost = res.post;
      } else {
        const res = await apiRequest<{ post: BackendPost }>('/posts', { method: 'POST', body: JSON.stringify(payload) }, true);
        updatedPost = res.post;
      }
      setPost(updatedPost);
      setData(formData);
      setIsEditing(false);
    } catch (error) {
      alert('Không thể lưu footer');
    } finally {
      setSaving(false);
    }
  }

  return (
    <footer className="footer" style={{
      position: 'relative',
      opacity: mounted ? 1 : 0,
      visibility: mounted ? 'visible' : 'hidden',
      transition: 'opacity 0.4s ease, visibility 0.4s ease',
    }}>
      {isAdmin && (
        <button 
          className="admin-action-btn" 
          style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
          onClick={() => {
            setFormData(JSON.parse(JSON.stringify(data)));
            setIsEditing(true);
          }}
        >
          <span className="icon">✎</span> Sửa Footer
        </button>
      )}

      <div className="container footer-grid">
        <div>
          <h4>{data.col1.title}</h4>
          <p>{data.col1.text}</p>
        </div>
        <div>
          <h4>{data.col2.title}</h4>
          <ul>
            {data.col2.links.map((link, i) => (
              <li key={i}>
                {link.url.startsWith('/') ? (
                  <Link to={link.url} style={{ color: 'inherit', textDecoration: 'none' }}>{link.label}</Link>
                ) : (
                  <a href={link.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{link.label}</a>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4>{data.col3.title}</h4>
          <ul>
            {data.col3.lines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="container" style={{
        borderTop: '1px solid rgba(255,255,255,0.1)',
        marginTop: '40px',
        paddingTop: '24px',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '14px',
        lineHeight: '1.6',
        paddingBottom: '24px'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 600, color: '#e2e8f0', letterSpacing: '1px' }}>
          3D SMART - H&amp;L STORE
        </div>
        <div style={{ marginBottom: '16px', fontSize: '12px', letterSpacing: '2px', color: '#64748b' }}>
          SÁNG TẠO • CHẤT LƯỢNG • TẬN TÂM
        </div>
        <div>
          &copy; {new Date().getFullYear()} 3D SMART - H&amp;L Store. All rights reserved.
        </div>
      </div>

      {isEditing && (
        <div className="modal-overlay" onClick={() => setIsEditing(false)} style={{ color: '#f8fafc', zIndex: 9999 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button className="modal-close" onClick={() => setIsEditing(false)}>×</button>
            <h2 style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px', color: '#f8fafc', margin: '0 0 24px 0' }}>Chỉnh sửa Footer</h2>
            <form onSubmit={onSave} className="admin-form">
              <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr' }}>
                
                {/* Column 1 */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '20px', borderRadius: '16px' }}>
                  <h3 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', color: '#ffffff', fontWeight: 700 }}>Cột 1: Thông tin thương hiệu</h3>
                  <label className="field-label">Tiêu đề</label>
                  <input className="input-box" value={formData.col1.title} onChange={e => setFormData({ ...formData, col1: { ...formData.col1, title: e.target.value } })} />
                  <label className="field-label" style={{ marginTop: '12px' }}>Nội dung mô tả</label>
                  <textarea className="input-box textarea" value={formData.col1.text} onChange={e => setFormData({ ...formData, col1: { ...formData.col1, text: e.target.value } })} />
                </div>

                {/* Column 2 */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '20px', borderRadius: '16px' }}>
                  <h3 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', color: '#ffffff', fontWeight: 700 }}>Cột 2: Liên kết / Danh mục</h3>
                  <label className="field-label">Tiêu đề</label>
                  <input className="input-box" value={formData.col2.title} onChange={e => setFormData({ ...formData, col2: { ...formData.col2, title: e.target.value } })} />
                  <label className="field-label" style={{ marginTop: '12px' }}>Danh sách liên kết</label>
                  {formData.col2.links.map((link, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input className="input-box" placeholder="Tên hiển thị" value={link.label} onChange={e => {
                        const newLinks = [...formData.col2.links];
                        newLinks[i] = { ...newLinks[i], label: e.target.value };
                        setFormData({ ...formData, col2: { ...formData.col2, links: newLinks } });
                      }} style={{ flex: 1, margin: 0 }} />
                      <input className="input-box" placeholder="URL (/duong-dan hoặc https://...)" value={link.url} onChange={e => {
                        const newLinks = [...formData.col2.links];
                        newLinks[i] = { ...newLinks[i], url: e.target.value };
                        setFormData({ ...formData, col2: { ...formData.col2, links: newLinks } });
                      }} style={{ flex: 2, margin: 0 }} />
                      <button type="button" className="button danger-outline" onClick={() => handleRemoveLink(i)} style={{ padding: '0 12px' }}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="button outline small" onClick={handleAddLink} style={{ marginTop: '8px' }}>+ Thêm liên kết</button>
                </div>

                {/* Column 3 */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '20px', borderRadius: '16px' }}>
                  <h3 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', color: '#ffffff', fontWeight: 700 }}>Cột 3: Liên hệ / Khác</h3>
                  <label className="field-label">Tiêu đề</label>
                  <input className="input-box" value={formData.col3.title} onChange={e => setFormData({ ...formData, col3: { ...formData.col3, title: e.target.value } })} />
                  <label className="field-label" style={{ marginTop: '12px' }}>Các dòng thông tin</label>
                  {formData.col3.lines.map((line, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input className="input-box" placeholder="Nội dung" value={line} onChange={e => {
                        const newLines = [...formData.col3.lines];
                        newLines[i] = e.target.value;
                        setFormData({ ...formData, col3: { ...formData.col3, lines: newLines } });
                      }} style={{ flex: 1, margin: 0 }} />
                      <button type="button" className="button danger-outline" onClick={() => handleRemoveLine(i)} style={{ padding: '0 12px' }}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="button outline small" onClick={handleAddLine} style={{ marginTop: '8px' }}>+ Thêm dòng</button>
                </div>
                
              </div>
              <div className="modal-actions" style={{ marginTop: '28px', borderTop: '1px solid var(--glass-border)', paddingTop: '20px' }}>
                <button className="button outline" type="button" onClick={() => setIsEditing(false)}>Hủy bỏ</button>
                <button className="button primary" type="submit" disabled={saving}>{saving ? 'Đang lưu...' : 'Lưu thay đổi'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
}
